import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    FileText,
    Upload,
    Eye,
    ArrowLeft,
    ArrowRight,
    Loader2,
    RotateCcw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import StepIndicator, { Step } from '@/components/StepIndicator'
import toast from 'react-hot-toast'

// Import step components
import Step1BasicInfo from './steps/Step1BasicInfo'
import Step2UploadFiles from './steps/Step2UploadFiles'
import Step3Review from './steps/Step3Review'

// Import custom hooks
import {
    useCreateAssetForm,
    AssetFormData,
    buildInitialAssetFormData,
} from './use-create-asset-form'
import { useCreateAssetTour } from './use-create-asset-tour'
import { useTourHandler } from '@/contexts/tour-handler'
import { useAssetDraftPersistence } from './use-asset-draft-persistence'
import DraftRestoreDialog from './DraftRestoreDialog'

// Re-export AssetFormData for child components
export type { AssetFormData }

interface CreateAssetFormProps {
    isUpdateMode?: boolean
}

const CreateAssetForm = ({ isUpdateMode = false }: CreateAssetFormProps) => {
    const { t } = useTranslation('asset')
    const navigate = useNavigate()
    const { id } = useParams() // asset id or slug

    const [currentStep, setCurrentStep] = useState(isUpdateMode ? 1 : 1)

    // Xóa draft localStorage khi server-draft được tạo / asset submit thành công.
    // Dùng ref vì callback được truyền vào useCreateAssetForm trước khi có clearDraft.
    const clearDraftRef = useRef<() => void>(() => {})
    // Bump để remount Step 1 sau khi khôi phục (subform ngày/chia sẻ đọc lại giá trị).
    const [restoreNonce, setRestoreNonce] = useState(0)

    // Use custom hook for business logic
    const {
        formData,
        setFormData,
        draftAsset,
        isFormReady,
        validateStep1,
        validateStep2,
        saveDraft,
        submitForm,
        isSaving,
        isSubmitting,
        versionBumpType,
        currentVersion,
        nextVersion,
        originalFormData,
        originalFileCount,
        revertChanges,
    } = useCreateAssetForm({
        id,
        isUpdateMode,
        onDraftSaved: () => {
            // Giữ draft localStorage để F5 ở Step 2 (hoặc back về Step 1) vẫn khôi phục được;
            // chỉ xóa khi submit thành công.
            setCurrentStep(2)
        },
        onAssetSubmitted: () => {
            clearDraftRef.current()
            navigate('/assets')
        },
    })

    // Save draft (localStorage) — chỉ ở chế độ tạo mới
    const draftDefaults = useMemo(() => buildInitialAssetFormData(), [])
    const { shouldRestore, acceptRestore, declineRestore, clearDraft } =
        useAssetDraftPersistence({
            formData,
            setFormData,
            defaultValues: draftDefaults,
            storageKey: 'asset-create-draftv1',
            // Bật xuyên suốt chế độ tạo mới (kể cả sau khi đã sang Step 2 / có server-draft)
            // để F5 ở bất kỳ bước nào vẫn khôi phục được dữ liệu Step 1.
            enabled: !id && !isUpdateMode,
        })
    clearDraftRef.current = clearDraft

    const handleAcceptRestore = () => {
        acceptRestore()
        setRestoreNonce((n) => n + 1)
    }

    // Tour setup
    const { startTour, isActive: isTourActive } = useCreateAssetTour({
        currentStep,
        setFormData,
    })
    const { registerHandler, unregisterHandler } = useTourHandler()

    // Register tour handler
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    const steps: Step[] = [
        {
            label: t('form.fillInfo'),
            icon: <FileText />,
            description: t('form.basicDetails'),
        },
        {
            label: t('form.uploadFiles'),
            icon: <Upload />,
            description: t('form.documents'),
        },
        {
            label: t('form.review'),
            icon: <Eye />,
            description: t('form.confirmSubmit'),
        },
    ]

    const handleStepChange = (step: number) => {
        // Validate current step before moving forward
        if (step > currentStep) {
            if (currentStep === 1 && !validateStep1()) {
                return
            }
            if (currentStep === 2 && !validateStep2()) {
                toast.error(t('form.uploadRequired'))
                return
            }
        }
        setCurrentStep(step)
    }

    const handleNext = () => {
        if (currentStep === 1) {
            if (isUpdateMode) {
                // Update mode: skip draft save, just validate and advance
                if (!validateStep1()) {
                    return
                }
                setCurrentStep(2)
            } else {
                saveDraft()
            }
        } else if (currentStep === 2) {
            if (!validateStep2()) {
                toast.error(t('form.uploadRequired'))
                return
            }
            handleStepChange(currentStep + 1)
        } else if (currentStep < steps.length) {
            handleStepChange(currentStep + 1)
        }
    }

    const handlePrevious = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1)
        }
    }

    const renderStepContent = () => {
        // Gate step rendering until draft -> formData hydration completes.
        if (!isFormReady) {
            return (
                <div className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{t('form.loading')}</span>
                </div>
            )
        }

        switch (currentStep) {
            case 1:
                return (
                    <Step1BasicInfo
                        formData={formData}
                        setFormData={setFormData}
                    />
                )
            case 2:
                return (
                    <Step2UploadFiles
                        formData={formData}
                        setFormData={setFormData}
                    />
                )
            case 3:
                return (
                    <Step3Review
                        formData={formData}
                        isUpdateMode={isUpdateMode}
                        versionBumpType={versionBumpType}
                        currentVersion={currentVersion}
                        nextVersion={nextVersion}
                        originalFormData={originalFormData}
                        originalFileCount={originalFileCount}
                    />
                )
            default:
                return null
        }
    }

    return (
        <div className="mx-auto space-y-4 ">
            <DraftRestoreDialog
                open={shouldRestore}
                onAccept={handleAcceptRestore}
                onDecline={declineRestore}
            />

            <div className="bg-white rounded-lg shadow-sm">
                <StepIndicator
                    steps={steps}
                    currentStep={currentStep}
                    onChange={handleStepChange}
                />
            </div>

            <div key={restoreNonce}>{renderStepContent()}</div>

            <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 sm:justify-between">
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={handlePrevious}
                            disabled={currentStep === 1}
                            className="w-full sm:w-auto"
                            data-tour="prev-button"
                        >
                            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
                            <span className="hidden sm:inline">
                                {t('form.previous')}
                            </span>
                            <span className="sm:hidden">{t('form.back')}</span>
                        </Button>

                        {isUpdateMode && versionBumpType !== 'none' && (
                            <Button
                                variant="outline"
                                onClick={revertChanges}
                                className="w-full sm:w-auto text-amber-600 border-amber-300 hover:bg-amber-50"
                            >
                                <RotateCcw className="w-4 h-4 mr-1 sm:mr-2" />
                                {t('form.revertChanges')}
                            </Button>
                        )}
                    </div>

                    {currentStep < steps.length ? (
                        <Button
                            onClick={handleNext}
                            disabled={currentStep === 1 && isSaving}
                            className="w-full sm:w-auto"
                            data-tour="next-button"
                        >
                            <span className="hidden sm:inline">
                                {currentStep === 1 && isSaving
                                    ? t('form.saving')
                                    : t('form.next')}
                            </span>
                            <span className="sm:hidden">
                                {currentStep === 1 && isSaving
                                    ? t('form.saving')
                                    : t('form.continue')}
                            </span>
                            <ArrowRight className="w-4 h-4 ml-1 sm:ml-2" />
                        </Button>
                    ) : (
                        <Button
                            onClick={submitForm}
                            disabled={
                                isSubmitting ||
                                (!isUpdateMode && !draftAsset) ||
                                isTourActive ||
                                (isUpdateMode && versionBumpType === 'none')
                            }
                            className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                            data-tour="submit-button"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                                    {t('form.submitting')}
                                </>
                            ) : isUpdateMode ? (
                                t('form.updateAsset')
                            ) : (
                                t('form.submitAsset')
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CreateAssetForm
